import {
  ITeamMember,
  ROLE_INFO,
  stripOwnerOnlyActionKeys,
  TeamRole,
} from '@/@types/TeamMember';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { TeamService } from '@/services/team.service';
import { useTeamActions } from '@/hooks/useTeamActions';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PermissionsAccordion } from './PermissionsAccordion';

interface EditPermissionsSheetProps {
  open: boolean;
  onClose: () => void;
  member: ITeamMember | null;
}

const ROLES: TeamRole[] = ['admin', 'viewer', 'financial', 'custom'];

export function EditPermissionsSheet({ open, onClose, member }: EditPermissionsSheetProps) {
  const { t } = useTranslation('team');
  const queryClient = useQueryClient();
  const { categories, rolePresets, isLoading: isLoadingActions } = useTeamActions();
  const [selectedRole, setSelectedRole] = useState<TeamRole>('viewer');
  const [permissions, setPermissions] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (member && rolePresets) {
      setSelectedRole(member.role);
      if (member.role !== 'custom') {
        setPermissions(stripOwnerOnlyActionKeys(rolePresets[member.role]));
      } else {
        setPermissions(stripOwnerOnlyActionKeys(member.permissions));
      }
    }
  }, [member, rolePresets]);

  const { mutateAsync: updatePermissions, isPending } = useMutation({
    mutationFn: (payload: { permissions: Record<string, string[]> }) =>
      TeamService.updatePermissions(member!.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast({ description: t('permissions_updated') });
      onClose();
    },
    onError: () => {
      toast({ variant: 'destructive', description: t('update_error') });
    },
  });

  const handleRoleSelect = (role: TeamRole) => {
    setSelectedRole(role);
    if (role !== 'custom' && rolePresets) {
      setPermissions(stripOwnerOnlyActionKeys(rolePresets[role]));
    }
  };

  const handlePermissionsChange = (newPermissions: Record<string, string[]>) => {
    if (selectedRole !== 'custom') {
      setSelectedRole('custom');
    }
    setPermissions(stripOwnerOnlyActionKeys(newPermissions));
  };

  const handleSave = async () => {
    const hasAtLeastOnePermission = Object.values(permissions).some(
      (perms) => perms.length > 0
    );

    if (!hasAtLeastOnePermission) {
      toast({
        variant: 'destructive',
        description: t('permissions_required', 'Selecione pelo menos uma permissão.'),
      });
      return;
    }

    await updatePermissions({ permissions: stripOwnerOnlyActionKeys(permissions) });
  };

  if (!member) return null;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="md:w-[504px] w-full h-[100vh] max-h-[100vh] overflow-hidden p-0">
        <section className="h-full flex flex-col">
          <div className="px-6 pt-6 pb-4 border-b border-colorBorder flex-shrink-0">
            <h2 className="text-lg font-semibold">{t('edit_permissions')}</h2>
            <div className="flex items-center gap-3 mt-3">
              <div className="w-10 h-10 rounded-full bg-elevated flex items-center justify-center text-sm font-medium text-muted">
                {(member.name || member.email).charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium">{member.name || member.email}</p>
                <p className="text-xs text-muted">{member.email}</p>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-3">{t('access_profile')}</h3>
              <div className="grid grid-cols-2 gap-2">
                {ROLES.map((role) => {
                  const info = ROLE_INFO[role];
                  const isSelected = selectedRole === role;
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => handleRoleSelect(role)}
                      className={cn(
                        'p-3 rounded-lg border text-left transition-all duration-200',
                        isSelected
                          ? 'border-primary/50 bg-primary/5'
                          : 'border-colorBorder bg-card hover:border-primary/30'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-base">{info.emoji}</span>
                        <span className="text-xs font-medium">{t(`roles.${role}`)}</span>
                        {isSelected && <Check className="w-3 h-3 text-primary ml-auto" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium mb-3">{t('detailed_permissions')}</h3>
              {isLoadingActions ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="animate-spin h-6 w-6 text-muted" />
                </div>
              ) : (
                <PermissionsAccordion
                  permissions={permissions}
                  onChange={handlePermissionsChange}
                  categories={categories}
                />
              )}
            </div>
          </div>

          <div className="flex items-center justify-between p-4 border-t border-colorBorder flex-shrink-0 max-sm:pb-12">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="text-zinc-400 hover:text-zinc-100"
            >
              {t('cancel')}
            </Button>
            <Button
              disabled={isPending}
              type="button"
              onClick={handleSave}
              className="px-6"
            >
              {isPending ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="animate-spin h-4 w-4" />
                  {t('saving')}
                </span>
              ) : (
                t('save')
              )}
            </Button>
          </div>
        </section>
      </SheetContent>
    </Sheet>
  );
}
